// Доступ: один ученик, секретный ключ в ссылке и в localStorage.
// Авторизации нет и в фазе 1 не будет: инвариант «эталонный ответ не уезжает на
// клиент» от неё не зависит. Ключ не логируется.
//
// Из адресной строки ключ уходит сразу, как только сохранён. Ссылка с `?k=`
// иначе оседает в истории браузера, в автодополнении адреса, в списке
// открытых вкладок и в любом скриншоте экрана — а взять её оттуда достаточно,
// чтобы отвечать за ученика и портить журнал попыток, который по инварианту 2
// не чистится никогда.
//
// Цена решения названа честно: закладка, сделанная после чистки, ключа не
// содержит, и на устройстве с очищенными данными сайта она не откроет ничего.
// Обратный путь — не история браузера, а `make key`: ключ лежит в `.env.local`
// у взрослого и читается из деплоя, если и там потерялся. Пока такого места не
// было, адресная строка работала последней копией ключа, и убирать её оттуда
// было нельзя.

import { ADMIN_PREFIX } from "./admin";
import { TUTOR_PREFIX } from "./tutor";

const STORAGE_KEY = "traektoria.learnerKey";
// Пробный ученик взрослого (convex/sandbox.ts). Его ключ живёт во вкладке, а
// не в localStorage: взрослый и сын делят браузер, и ключ пробного ученика,
// легший на место ключа сына, увёл бы сына в чужой журнал — а его собственный
// ключ был бы потерян. Префикс тот же, что `SANDBOX_PREFIX` на сервере.
const SANDBOX_PREFIX = "s-";
const SANDBOX_STORAGE_KEY = "traektoria.sandboxKey";

function sandboxKey(): string | null {
  try {
    return window.sessionStorage.getItem(SANDBOX_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Вкладка пробного ученика: экран ученика, но решает взрослый. */
export function isSandboxKey(key: string): boolean {
  return key.startsWith(SANDBOX_PREFIX);
}
// Ключа нет — пустая строка, а не чужой ключ и не «первый попавшийся
// ученик». Запрос с ней до сервера не идёт: App.tsx показывает экран
// «ссылка без ключа» (src/ui/NoKey.tsx), не спрашивая Convex ни о чём.
const FALLBACK = "";

/** Сохранить ключ. `false` — приватное окно: писать некуда. */
function store(key: string): boolean {
  try {
    window.localStorage.setItem(STORAGE_KEY, key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Убрать `k` из адреса, сохранив остальные параметры.
 *
 * Вырезается ровно один параметр, а не переписывается запрос: `?control=1`,
 * `?map=1` и `?scratch=` читаются в App.tsx уже после этого вызова, и потеря
 * любого из них увела бы взрослого не на тот экран.
 */
function dropParam(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("k");
    window.history.replaceState(null, "", url.toString());
  } catch {
    // Адрес не переписался — на доступ это не влияет, ключ уже сохранён.
  }
}

export function readLearnerKey(): string {
  const fromLink = new URLSearchParams(window.location.search).get("k");
  // Ключ взрослого сюда не попадает ни при каких условиях. Выбор экрана
  // делается раньше, в src/main.tsx, и до этой строки дело обычно не доходит
  // — но сохранение здесь необратимо: ключ взрослого, легший в localStorage
  // общего браузера, затирает ключ ребёнка, а вместе с ним доступ к журналу
  // занятий, который по инварианту 2 не восстановить ничем.
  // Ключ разработчика (src/lib/admin.ts) — по той же причине.
  if (
    fromLink !== null &&
    (fromLink.startsWith(TUTOR_PREFIX) || fromLink.startsWith(ADMIN_PREFIX))
  ) {
    return FALLBACK;
  }
  if (fromLink !== null && isSandboxKey(fromLink)) {
    try {
      window.sessionStorage.setItem(SANDBOX_STORAGE_KEY, fromLink);
      dropParam();
    } catch {
      // Писать некуда — ключ остаётся в адресе, как в приватном окне.
    }
    return fromLink;
  }
  // Вкладка, однажды открытая пробным учеником, им и остаётся после
  // обновления страницы; остальные вкладки этого ключа не видят.
  const inTab = sandboxKey();
  if (fromLink === null && inTab !== null) return inTab;
  if (fromLink) {
    // Чистится только сохранённый ключ. В приватном окне localStorage
    // недоступен, ссылка остаётся единственным носителем ключа, и вычистить
    // её — значит выбросить ученика на первом же обновлении страницы.
    if (store(fromLink)) dropParam();
    return fromLink;
  }
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? FALLBACK;
  } catch {
    return FALLBACK;
  }
}
